import {
  breakpointSizes,
  getBreakpointUp,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectVerifyPhoneTopLevelErrorFromState } from 'src/app/Signup/redux/signup.selectors'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SecondaryEmailLink from 'src/components/SecondaryEmailLink/SecondaryEmailLink'
import TestId from 'src/components/TestId/TestId'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useFormFields, useScrollToScrollGroupRefs } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { SecondaryEmailResponse, UserListModel } from 'src/models/user.models'
import { peopleMembersDetailUrl } from 'src/navigation/urls'
import { useUserDeletePhoneNumber } from 'src/queries/users.queries'
import {
  createSetIsUpdatePhoneNumberPopupOpen,
  createSetIsVerifyPhoneNumberPopupOpen,
} from 'src/redux/actions/app.actions'
import { postAuthSendPhoneVerificationCode } from 'src/redux/actions/auth.actions'
import { deleteSecondaryEmail } from 'src/redux/actions/data/users.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFetchingSecondaryEmailsFromState } from 'src/redux/selectors/data/secondaryEmails.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { formatPhoneNumber } from 'src/utils/string.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type EditProfileInfoFlatFields = {
  firstName?: string
  lastName?: string
}

interface EditProfileInfoFormProps {
  className?: string
  user: UserListModel
  secondaryEmails: SecondaryEmailResponse[]
  patchingUser: boolean
  patchingUserProfile: boolean
  errorDetailMap?: ErrorDetailMap<string>
  onSuccessfulSubmit: (flatFields: EditProfileInfoFlatFields) => void
  isEditing: boolean
  setIsEditing: (val: boolean) => void
}

const EditProfileInfoForm: React.FC<EditProfileInfoFormProps> = (
  props,
): React.ReactElement => {
  const fetchingSecondaryEmails = useSelector(
    selectFetchingSecondaryEmailsFromState,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const verifyPhoneTopLevelError = useSelector(
    selectVerifyPhoneTopLevelErrorFromState,
  )

  const [removeSecondaryEmail, setRemoveSecondaryEmail] =
    useState<boolean>(false)
  const [removePhoneNumber, setRemovePhoneNumber] = useState<boolean>(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)

  const { analytics } = useAnalyticsContext()

  const dispatch = useDispatch()

  const {
    fields: flatFields,
    setFields: setFlatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
    topmostScrollGroup: flatFieldsTopmostScrollGroup,
  } = useFormFields<EditProfileInfoFlatFields>( // TODO: should make this string | undefined to be totally transparent
    {},
    () => ({
      firstName: [stringIsEmpty()],
      lastName: [stringIsEmpty()],
    }),
    props.errorDetailMap,
  )
  useEffect((): void => {
    setFlatFields({
      firstName: props.user.firstName,
      lastName: props.user.lastName,
    })
  }, [props.user])

  const scrollRef0 = useRef<HTMLFormElement>(null)
  useScrollToScrollGroupRefs([scrollRef0], [flatFieldsTopmostScrollGroup])

  const { mutate: deletePhoneNumber } = useUserDeletePhoneNumber()

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = flatFieldsSetErrors()

    if (flatFieldsPassedValidation) {
      if (removeSecondaryEmail) {
        dispatch(deleteSecondaryEmail(props.secondaryEmails[0].email))
        setRemoveSecondaryEmail(false)
      } else if (removePhoneNumber) {
        deletePhoneNumber({ body: {} })
        setRemovePhoneNumber(false)
      }
      props.onSuccessfulSubmit(flatFields)
      props.setIsEditing(false)
    }
  }

  useEffect((): void => {
    if (props.patchingUser) {
      setHasSubmitted(false)
    }
  }, [props.patchingUser])

  useEffect((): void => {
    if (props.isEditing && !props.patchingUserProfile) {
      setHasSubmitted(true)
      props.setIsEditing(false)
    }
  }, [props.patchingUserProfile])

  useEffect((): void => {
    if (hasSubmitted) {
      props.setIsEditing(true)
    }
  }, [flatFieldsErrors])

  return (
    <form
      ref={scrollRef0}
      onSubmit={onSubmit}
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <FieldLabel>
        <Trns source={'Business Email'} />
      </FieldLabel>
      <HoverableLink
        to={peopleMembersDetailUrl(props.user.id)}
        underline
        ariaLabel={props.user.email}
        size={HoverableLinkSizes.Large}
      >
        {props.user.email}
      </HoverableLink>
      {props.secondaryEmails && (
        <div
          css={css`
            margin-top: 24px;
          `}
        >
          <div
            css={css`
              display: flex;
              gap: 20px;
              align-items: flex-start;
            `}
          >
            <FieldLabel>
              <Trns source={'Secondary Email'} />
            </FieldLabel>
            {props.isEditing &&
              props.secondaryEmails[0]?.email !== undefined &&
              (!removeSecondaryEmail ? (
                <HoverableLink
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.regular,
                    )};
                    &:hover {
                      ${getFontCss(
                        14,
                        styleConstants.colors.red_dark,
                        styleConstants.fontFamilies.regular,
                      )};
                    }
                  `}
                  underline
                  regular
                  size={HoverableLinkSizes.Medium}
                  ariaLabelSource={'Remove'}
                  onClick={(): void => {
                    setRemoveSecondaryEmail(true)
                    analytics?.track('secondary user email - remove')
                  }}
                >
                  <TestId testId={'removeButton'}>
                    <Trns source={'Remove'} />
                  </TestId>
                </HoverableLink>
              ) : (
                <HoverableLink
                  underline
                  regular
                  size={HoverableLinkSizes.Medium}
                  ariaLabelSource={'Undo'}
                  onClick={(): void => {
                    setRemoveSecondaryEmail(false)
                  }}
                >
                  <Trns source={'Undo'} />
                </HoverableLink>
              ))}
          </div>
          <SecondaryEmailLink
            loading={fetchingSecondaryEmails}
            secondaryEmails={props.secondaryEmails}
            isEditing={props.isEditing}
            removeSecondaryEmail={removeSecondaryEmail}
          />
        </div>
      )}
      <div
        css={css`
          display: flex;
          margin-top: 24px;
          gap: 20px;
          align-items: flex-start;
        `}
      >
        <FieldLabel>
          <Trns source={'Phone Number'} />
        </FieldLabel>
        {props.isEditing &&
          props.user?.phone &&
          (!removePhoneNumber ? (
            <HoverableLink
              css={css`
                ${getFontCss(
                  14,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.regular,
                )};
                &:hover {
                  ${getFontCss(
                    14,
                    styleConstants.colors.red_dark,
                    styleConstants.fontFamilies.regular,
                  )};
                }
              `}
              underline
              regular
              size={HoverableLinkSizes.Medium}
              ariaLabelSource={'Remove'}
              onClick={(): void => {
                setRemovePhoneNumber(true)
                analytics?.track('phone number - remove')
              }}
            >
              <TestId testId={'removeButton'}>
                <Trns source={'Remove'} />
              </TestId>
            </HoverableLink>
          ) : (
            <HoverableLink
              underline
              regular
              size={HoverableLinkSizes.Medium}
              ariaLabelSource={'Undo'}
              onClick={(): void => {
                setRemovePhoneNumber(false)
              }}
            >
              <Trns source={'Undo'} />
            </HoverableLink>
          ))}
      </div>
      <div
        css={css`
          display: flex;
          gap: 20px;
        `}
      >
        {props.user?.phone && (
          <span
            css={css`
              ${removePhoneNumber &&
              css`
                text-decoration: line-through;
                ${getFontCss(
                  16,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            `}
          >
            {formatPhoneNumber(props.user?.phone)}
          </span>
        )}
        {!props.user.phone && (
          <div
            css={css`
              display: flex;
            `}
          >
            <TestId testId={'addPhoneNumberLink'}>
              <HoverableLink
                css={css`
                  display: flex;
                  flex-direction: row;
                `}
                onClick={(): void => {
                  dispatch(createSetIsUpdatePhoneNumberPopupOpen(true))
                  analytics?.track('add phone - profile settings')
                }}
                underline
                regular
                size={HoverableLinkSizes.Large}
                ariaLabelSource={'Add a phone number'}
              >
                {'+ '}
                <Trns source={'Add a phone number'} />
              </HoverableLink>
            </TestId>
          </div>
        )}
        {!props.isEditing &&
          props.user?.phone &&
          !props.user?.phoneVerified && (
            <HoverableLink
              css={css`
                margin-left: -10px;
              `}
              onClick={(): void => {
                dispatch(
                  postAuthSendPhoneVerificationCode(
                    props.user.email,
                    props.user.phone,
                  ),
                )
                if (!verifyPhoneTopLevelError) {
                  dispatch(createSetIsVerifyPhoneNumberPopupOpen(true))
                }
                analytics?.track('verify phone - profile settings')
              }}
              ariaLabelSource={'Verify'}
            >
              <Trns
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
                source={'Verify'}
              />
            </HoverableLink>
          )}
      </div>
      <FieldLabel
        css={css`
          margin-top: 24px;
        `}
      >
        <Trns source={'First Name'} />
      </FieldLabel>
      {props.isEditing ? (
        <TextInputField
          design={TextInputFieldDesigns.New}
          errors={flatFieldsErrors['firstName']}
          placeholder={translateSingleString(locale, 'Enter first name')}
          value={flatFields['firstName']}
          onChange={(val: string): void => onFlatFieldChange('firstName', val)}
        />
      ) : (
        <span>{flatFields['firstName']}</span>
      )}
      <FieldLabel
        css={css`
          margin-top: 24px;
        `}
      >
        <Trns source={'Last Name'} />
      </FieldLabel>
      {props.isEditing ? (
        <TextInputField
          design={TextInputFieldDesigns.New}
          errors={flatFieldsErrors['lastName']}
          placeholder={translateSingleString(locale, 'Enter last name')}
          value={flatFields['lastName']}
          onChange={(val: string): void => onFlatFieldChange('lastName', val)}
        />
      ) : (
        <span>{flatFields['lastName']}</span>
      )}
      {props.isEditing && (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 40px;
            margin-top: 40px;
          `}
        >
          <TestId testId={'saveChangesButton'}>
            <Button
              css={css`
                ${getBreakpointUp(breakpointSizes.SM)(css`
                  align-self: flex-start;
                `)}
              `}
              design={ButtonDesigns.designCta}
              type={ButtonTypes.submit}
              text={<Trns source={'Save Changes'} />}
              loading={props.patchingUserProfile}
              disabled={props.patchingUser}
              big
            />
          </TestId>
          <HoverableLink
            onClick={(): void => {
              props.setIsEditing(false)
              removeSecondaryEmail && setRemoveSecondaryEmail(false)
              removePhoneNumber && setRemovePhoneNumber(false)
            }}
            underline
            ariaLabelSource={'Cancel'}
          >
            <Trns source={'Cancel'} />
          </HoverableLink>
        </div>
      )}
    </form>
  )
}

export default EditProfileInfoForm
